import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './EmbedCodeEditor.css';
import { FaTimes, FaCode, FaHtml5, FaCss3, FaJs, FaSave, FaLightbulb } from 'react-icons/fa';

// Sample embed codes for testing
const SAMPLE_EMBEDS = {
  youtube: `<!-- YouTube Video Embed -->
<iframe 
  width="100%" 
  height="315" 
  src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
  title="YouTube video player" 
  frameborder="0" 
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
  allowfullscreen>
</iframe>`,
  
  map: `<!-- Google Maps Embed -->
<iframe 
  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d387193.30591910525!2d-74.25986432666163!3d40.697149422113014!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2s!4v1659702446377!5m2!1sen!2s" 
  width="100%" 
  height="300" 
  style="border:0;" 
  allowfullscreen="" 
  loading="lazy" 
  referrerpolicy="no-referrer-when-downgrade">
</iframe>`,
  
  interactive: `<!-- Interactive Widget with CSS and JavaScript -->
<div class="widget-container">
  <h2 class="widget-title">Interactive Counter</h2>
  <p class="counter-value">0</p>
  <div class="button-group">
    <button class="counter-button decrement">-</button>
    <button class="counter-button increment">+</button>
  </div>
</div>

<style>
  .widget-container {
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    background: linear-gradient(135deg, #6e8efb, #a777e3);
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 20px;
    text-align: center;
    color: white;
    max-width: 300px;
    margin: 0 auto;
  }
  
  .widget-title {
    margin-top: 0;
    font-size: 20px;
    font-weight: 600;
  }
  
  .counter-value {
    font-size: 36px;
    font-weight: bold;
    margin: 20px 0;
  }
  
  .button-group {
    display: flex;
    justify-content: center;
    gap: 10px;
  }
  
  .counter-button {
    background-color: white;
    color: #6e8efb;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 20px;
    cursor: pointer;
    transition: transform 0.2s;
  }
  
  .counter-button:hover {
    transform: scale(1.1);
  }
</style>

<script>
  document.addEventListener('DOMContentLoaded', function() {
    const counterValue = document.querySelector('.counter-value');
    const incrementBtn = document.querySelector('.increment');
    const decrementBtn = document.querySelector('.decrement');
    
    let count = 0;
    
    incrementBtn.addEventListener('click', function() {
      count++;
      counterValue.textContent = count;
    });
    
    decrementBtn.addEventListener('click', function() {
      count--;
      counterValue.textContent = count;
    });
  });
</script>`
};

const EmbedCodeEditor = ({
  value,
  onChange,
  onClose,
  onSave
}) => {
  const editorRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [code, setCode] = useState(value || '');
  const [mode, setMode] = useState('editor'); // 'editor' or 'preview'
  const [showSamples, setShowSamples] = useState(false);

  // Initialize position for draggable editor
  useEffect(() => {
    // Center the editor initially
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    setPosition({
      x: (screenWidth - 800) / 2, // 800px is max-width of editor
      y: (screenHeight - 600) / 2 // 600px is height of editor
    });
  }, []);

  // Add drag event listeners
  useEffect(() => {
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging]);

  // Handle mouse down for dragging
  const handleDragStart = (e) => {
    if (e.target.closest('.embed-editor-header')) {
      setIsDragging(true);
      setDragStartPos({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      e.preventDefault();
    }
  };

  // Handle mouse move for dragging
  const handleDragMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStartPos.x,
        y: e.clientY - dragStartPos.y
      });
      e.preventDefault();
    }
  };

  // Handle mouse up for dragging
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Handle saving
  const handleSave = () => {
    if (onSave) {
      onSave(code);
    }
    if (onClose) {
      onClose();
    }
  };

  // Handle code change
  const handleCodeChange = (e) => {
    setCode(e.target.value);
    if (onChange) {
      onChange(e.target.value);
    }
  };

  // Toggle between editor and preview
  const toggleMode = () => {
    setMode(mode === 'editor' ? 'preview' : 'editor');
  };

  // Apply sample code
  const applySample = (sampleKey) => {
    const newCode = SAMPLE_EMBEDS[sampleKey];
    setCode(newCode);
    if (onChange) {
      onChange(newCode);
    }
    setShowSamples(false);
  };

  // Toggle samples dropdown
  const toggleSamples = () => {
    setShowSamples(!showSamples);
  };

  // Create sandbox HTML for the preview
  const createPreviewHtml = () => {
    return { __html: code };
  };

  // Render the editor in a portal at the root level
  return ReactDOM.createPortal(
    <div 
      className="embed-code-editor-container"
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        zIndex: 9999
      }}
      onMouseDown={handleDragStart}
    >
      <div className="embed-editor-header">
        <div className="embed-editor-title">
          <FaCode /> Embed Code Editor
        </div>
        <div className="embed-editor-tabs">
          <button 
            className={`embed-editor-tab ${mode === 'editor' ? 'active' : ''}`} 
            onClick={() => setMode('editor')}
          >
            <FaCode /> Editor
          </button>
          <button 
            className={`embed-editor-tab ${mode === 'preview' ? 'active' : ''}`} 
            onClick={() => setMode('preview')}
          >
            Preview
          </button>
        </div>
        <div className="embed-editor-actions">
          <div className="sample-dropdown-container">
            <button className="embed-editor-sample" onClick={toggleSamples}>
              <FaLightbulb /> Try Sample
            </button>
            {showSamples && (
              <div className="sample-dropdown">
                <button onClick={() => applySample('youtube')}>YouTube Video</button>
                <button onClick={() => applySample('map')}>Google Map</button>
                <button onClick={() => applySample('interactive')}>Interactive Widget</button>
              </div>
            )}
          </div>
          <button className="embed-editor-save" onClick={handleSave}>
            <FaSave /> Save
          </button>
          <button className="embed-editor-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
      </div>
      
      <div className="embed-editor-content">
        {mode === 'editor' ? (
          <div className="embed-code-editor">
            <div className="embed-code-toolbar">
              <div className="embed-code-info">
                Add custom HTML, CSS, or JavaScript code that will be embedded on the published site.
              </div>
              <div className="embed-code-icons">
                <FaHtml5 className="html-icon" title="HTML" />
                <FaCss3 className="css-icon" title="CSS" />
                <FaJs className="js-icon" title="JavaScript" />
              </div>
            </div>
            <textarea
              ref={editorRef}
              className="embed-code-textarea"
              value={code}
              onChange={handleCodeChange}
              placeholder="<!-- Paste your embed code here -->"
              spellCheck="false"
            ></textarea>
          </div>
        ) : (
          <div className="embed-code-preview">
            <div className="embed-preview-wrapper">
              {code ? (
                <div className="preview-content" dangerouslySetInnerHTML={createPreviewHtml()} />
              ) : (
                <div className="empty-preview">
                  <FaCode size={40} />
                  <p>Add some code to see a preview</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EmbedCodeEditor; 